import React, { memo, useMemo, useState, useEffect, useCallback } from 'react';
import { FlatList } from 'react-native';
import useColors from 'hooks/useColors';
import { useAppSelector } from 'store/hooks';
import { selectAllCurrentAccounts, selectAllInvestmentAccounts, selectAllSavingAccounts, selectIsGBUser, } from 'reducers/selectors';
import { overviewColorMap } from 'features/accounts/constants';
import useMonevoEntry from 'features/borrow/monevo/hooks/useMonevoEntry';
import { useAddAccountModal } from 'features/accounts/hooks/useAddAccountModal';
import { sortAccounts } from 'utils/api/sortAccounts';
import { selectUnopenedPotsInfo } from 'features/pots/reducers/selectors';
import { selectUnopenedInvestmentAccounts } from 'features/invest/reducers/selectors';
import i18n from '../../../../i18n';
import AccountOverviewItem from './AccountOverviewItem';
import AccountOverviewEmpty from './AccountOverviewEmpty';
import AccountOverviewFooter from './AccountOverviewFooter';
import { makeSelectOverviewHiddenSections, makeSelectOverviewClosedSections, selectTotalHiddenCount, selectTotalClosedCount, } from '../../selectors/overviewSections';
const keyExtractor = (item) => item.id.toString();
const markLastItem = (arr) => {
    const last = arr.pop();
    if (last) {
        arr.push({ ...last, isLast: true });
    }
};
const AccountsOverviewSection = ({ title, accounts, onRowPress, initialTitle, onOpenLogins, onOpenUpgrade, onRowLongPress, onContentSizeChange, }) => {
    const isGBUser = useAppSelector(selectIsGBUser);
    const openMonevo = useMonevoEntry();
    const unopenedPots = useAppSelector(selectUnopenedPotsInfo);
    const unopenedInvestmentAccounts = useAppSelector(selectUnopenedInvestmentAccounts);
    const addAccount = useAddAccountModal('Everyday');
    const [sectionShown, setSectionShown] = useState(title === initialTitle);
    useEffect(() => {
        if (!sectionShown) {
            const timeout = setTimeout(() => {
                setSectionShown(true);
            }, 400);
            return () => {
                clearTimeout(timeout);
            };
        }
        return () => { };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const accountsSelector = useMemo(() => {
        switch (title) {
            case 'Everyday':
                return selectAllCurrentAccounts;
            case 'Savings':
                return selectAllSavingAccounts;
            case 'Investments':
                return selectAllInvestmentAccounts;
            default:
                return selectAllCurrentAccounts;
        }
    }, [title]);
    // @ts-ignore
    const sectionAccounts = useAppSelector(accountsSelector);
    const hiddenSectionsSelector = useMemo(() => makeSelectOverviewHiddenSections(title), [title]);
    const closedSectionsSelector = useMemo(() => makeSelectOverviewClosedSections(title), [title]);
    const hiddenSections = useAppSelector(hiddenSectionsSelector);
    const closedSections = useAppSelector(closedSectionsSelector);
    const hiddenCount = selectTotalHiddenCount(hiddenSections);
    const closedCount = selectTotalClosedCount(closedSections);
    const rowPress = React.useCallback((data, touchViewRef) => {
        onRowPress(data, touchViewRef);
    }, [onRowPress]);
    const colors = useColors();
    const sectionData = useMemo(() => {
        const data = [];
        if (!sectionShown)
            return data;
        switch (title) {
            case 'Everyday': {
                if (sectionAccounts.array.length) {
                    const everydayArray = sortAccounts([
                        ...sectionAccounts.array,
                    ]);
                    data.push({
                        id: 'everyday',
                        balanceColor: colors.text[overviewColorMap.Checking],
                        total: sectionAccounts.total,
                        title: i18n.t('current'),
                        firstItem: everydayArray[0],
                        isEmpty: everydayArray.length === 0,
                    });
                    if (everydayArray.length) {
                        markLastItem(everydayArray);
                        data.push(...everydayArray);
                    }
                }
                if (accounts.CREDITCARD.array.length || isGBUser) {
                    const reversed = sortAccounts([...accounts.CREDITCARD.array], true);
                    data.push({
                        id: 'everyday-credit-card',
                        firstCTA: '+ Add a new card',
                        secondCTA: 'find one',
                        forceShowBottomContent: !reversed.length,
                        balanceColor: colors.text[overviewColorMap.Debt],
                        title: 'Credit cards',
                        total: accounts.CREDITCARD.total,
                        firstItem: reversed[0],
                        isEmpty: reversed.length === 0,
                    });
                    if (reversed.length) {
                        markLastItem(reversed);
                        data.push(...reversed);
                    }
                }
                break;
            }
            // Cash is a combination of savings and checking (used if the user has investments)
            case 'Cash': {
                if (accounts.CHECKING.array.length) {
                    const checkingArray = sortAccounts([
                        ...accounts.CHECKING.array,
                    ]);
                    data.push({
                        id: 'cash-checking',
                        balanceColor: colors.text[overviewColorMap.Checking],
                        title: i18n.t('current'),
                        total: accounts.CHECKING.total,
                        firstItem: checkingArray[0],
                        isEmpty: checkingArray.length === 0,
                    });
                    if (checkingArray.length) {
                        markLastItem(checkingArray);
                        data.push(...checkingArray);
                    }
                }
                if (accounts.SAVINGS.array.length) {
                    const savingsArray = sortAccounts([
                        ...accounts.SAVINGS.array,
                    ]);
                    data.push({
                        id: 'cash-savings',
                        balanceColor: colors.text[overviewColorMap.Savings],
                        title: 'Savings',
                        total: accounts.SAVINGS.total,
                        firstItem: savingsArray[0],
                        isEmpty: savingsArray.length === 0,
                    });
                    if (savingsArray.length) {
                        markLastItem(savingsArray);
                        data.push(...savingsArray);
                    }
                }
                break;
            }
            case 'Checking':
            case 'Current': {
                if (accounts.CHECKING.array.length) {
                    const checkingArray = sortAccounts([
                        ...accounts.CHECKING.array,
                    ]);
                    data.push({
                        id: 'current-checking',
                        balanceColor: colors.text[overviewColorMap.Checking],
                        title: i18n.t('current'),
                        total: accounts.CHECKING.total,
                        firstItem: checkingArray[0],
                        isEmpty: checkingArray.length === 0,
                    });
                    markLastItem(checkingArray);
                    data.push(...checkingArray);
                }
                break;
            }
            case 'Debt': {
                if (accounts.LOAN.array.length) {
                    const reversed = sortAccounts([...accounts.LOAN.array], true);
                    data.push({
                        id: 'debt-loan',
                        balanceColor: colors.text[overviewColorMap.Debt],
                        title: 'Loans',
                        total: accounts.LOAN.total,
                        firstItem: reversed[0],
                        isEmpty: reversed.length === 0,
                    });
                    if (reversed.length) {
                        markLastItem(reversed);
                        data.push(...reversed);
                    }
                }
                if (accounts.CREDITCARD.array.length) {
                    const reversed = sortAccounts([...accounts.CREDITCARD.array], true);
                    data.push({
                        id: 'debt-credit-card',
                        balanceColor: colors.text[overviewColorMap.Debt],
                        title: 'Credit cards',
                        total: accounts.CREDITCARD.total,
                        firstItem: reversed[0],
                        isEmpty: reversed.length === 0,
                    });
                    if (reversed.length) {
                        markLastItem(reversed);
                        data.push(...reversed);
                    }
                }
                break;
            }
            case 'Savings': {
                if (sectionAccounts.array.length) {
                    const savingsAccounts = sortAccounts([
                        ...sectionAccounts.array,
                    ]);
                    data.push({
                        id: 'savings',
                        balanceColor: colors.text[overviewColorMap.Savings],
                        title: 'Savings',
                        total: sectionAccounts.total,
                        firstItem: savingsAccounts[0],
                        isEmpty: savingsAccounts.length === 0,
                    });
                    if (savingsAccounts.length) {
                        markLastItem(savingsAccounts);
                        data.push(...savingsAccounts);
                    }
                    if (unopenedPots.length) {
                        data.push(...unopenedPots);
                    }
                }
                break;
            }
            case 'Investments': {
                if (sectionAccounts.array.length) {
                    const investmentAccounts = sortAccounts([...sectionAccounts.array]);
                    data.push({
                        id: 'investments',
                        balanceColor: colors.text[overviewColorMap.Investments],
                        title: 'Investments',
                        total: sectionAccounts.total,
                        firstItem: investmentAccounts[0],
                        isEmpty: investmentAccounts.length === 0,
                    });
                    if (investmentAccounts.length) {
                        markLastItem(investmentAccounts);
                        data.push(...investmentAccounts);
                        if (unopenedInvestmentAccounts.length) {
                            data.push(...unopenedInvestmentAccounts);
                        }
                    }
                }
                if (accounts.CRYPTO.array.length) {
                    const cryptoAccounts = sortAccounts([
                        ...accounts.CRYPTO.array,
                    ]);
                    data.push({
                        id: 'investments-crypto',
                        balanceColor: colors.text[overviewColorMap.Investments],
                        title: 'Crypto',
                        total: accounts.CRYPTO.total,
                        firstItem: cryptoAccounts[0],
                        isEmpty: cryptoAccounts.length === 0,
                    });
                    if (cryptoAccounts.length) {
                        markLastItem(cryptoAccounts);
                        data.push(...cryptoAccounts);
                    }
                }
                break;
            }
            default:
                break;
        }
        return data;
    }, [
        accounts.CHECKING,
        accounts.CREDITCARD,
        accounts.CRYPTO,
        accounts.LOAN,
        accounts.SAVINGS,
        colors.text,
        isGBUser,
        sectionAccounts,
        sectionShown,
        title,
        unopenedPots,
        unopenedInvestmentAccounts,
    ]);
    const renderItem = useCallback(({ item }) => (<AccountOverviewItem {...item} onRowPress={rowPress} onRowLongPress={onRowLongPress} onOpenLogins={onOpenLogins} onOpenUpgrade={onOpenUpgrade} onFirstCTAPress={() => addAccount()} onSecondCTAPress={() => {
            openMonevo('CREDIT_CARD');
        }}/>), [
        addAccount,
        onOpenLogins,
        onOpenUpgrade,
        onRowLongPress,
        openMonevo,
        rowPress,
    ]);
    const ListFooterComponent = useMemo(() => (<AccountOverviewFooter title={title} hiddenCount={hiddenCount} closedCount={closedCount}/>), [title, hiddenCount, closedCount]);
    const ListEmptyComponent = useMemo(() => <AccountOverviewEmpty title={title}/>, [title]);
    return (<FlatList data={sectionData} scrollEnabled={false} renderItem={renderItem} keyExtractor={keyExtractor} onContentSizeChange={onContentSizeChange} ListFooterComponent={ListFooterComponent} ListEmptyComponent={ListEmptyComponent}/>);
};
export default memo(AccountsOverviewSection);
